import {
  Popover,
  PopoverDisclosure,
  PopoverProvider
} from '@ariakit/react/popover';
import { domAnimation, motion } from 'framer-motion';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Menu as MenuType, MenuItem, SubMenu } from 'types/capi/plp';

import ChevronIcon from '~/assets/icons/Chevron';
import FramerMotion from '~/components/FramerMotion';
import useRegion from '~/hooks/useRegion';
import type { TrackedButtonClickName } from '~/services/gtm.client';
import gtm from '~/services/gtm.client';
import type { Region } from '~/utils/region';

import MenuOptionsList from './MenuOptionsList';
import SizeFilterGrid from './SizeMenuGrid';

type Props = {
  menus: MenuType[];
};

function getMenuDisplayTitle({
  activeItem,
  menu,
  region,
  translate
}: {
  activeItem: MenuItem | undefined;
  menu: MenuType | SubMenu;
  region: Region;
  translate: TFunction<'plp', undefined>;
}) {
  if (!activeItem) {
    return menu.id === 'GENDER_MENU' ? translate('gender') : menu.title;
  }

  if (menu.id === 'SIZE_MENU') {
    return `${region.code === 'GB' ? 'UK' : 'EU'} ${activeItem.text}`;
  }

  return activeItem.text;
}

function getEventTitleName(title: string) {
  return title === 'Discover' ? 'Gender' : title;
}

function Dropdown({
  menu,
  activeItem
}: {
  activeItem?: MenuItem;
  menu: MenuType | SubMenu;
}) {
  const { t } = useTranslation('plp');
  const region = useRegion();
  const [isOpen, setIsOpen] = useState(false);
  const isSizeFilter = menu.id === 'SIZE_MENU';

  const eventTitleName = getEventTitleName(menu.title);

  function onMenuClick() {
    setIsOpen(!isOpen);

    return gtm.trackedButtonClick({
      buttonName: eventTitleName as TrackedButtonClickName,
      pagePosition: 'Top Bar'
    });
  }

  function onOptionSelect(value: string) {
    setIsOpen(false);

    gtm.sendPlpProductFilterEvent({
      filterName: eventTitleName,
      filterValue: value,
      pagePosition: 'Filter Toolbar'
    });
  }

  const menuDisplayTitle = getMenuDisplayTitle({
    activeItem,
    menu,
    region,
    translate: t
  });

  return (
    <PopoverProvider open={isOpen}>
      <PopoverDisclosure
        onClick={onMenuClick}
        className={`flex h-8 w-full items-center gap-2 rounded-lg px-4 py-2 text-center font-semibold transition duration-200 ease-in-out active:scale-95 ${activeItem ? 'bg-black' : isSizeFilter ? 'bg-forest' : 'bg-white'} rounded-lg border border-grey-divide px-4`}
      >
        <p
          className={`whitespace-nowrap ${activeItem ? 'text-white' : isSizeFilter ? 'text-white' : 'text-grey-hide'} text-xs leading-5`}
        >
          {menuDisplayTitle}
        </p>
        <div className={isOpen ? 'rotate-180' : ''}>
          <ChevronIcon stroke="#A9A9A9" width="10" height="10" />{' '}
        </div>
      </PopoverDisclosure>
      <FramerMotion feature={domAnimation}>
        <Popover
          render={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
          }
          onClose={() => setIsOpen(false)}
          className={`z-40 w-2/3 max-w-lg rounded-md bg-white shadow-lg ${isSizeFilter ? 'min-w-fit' : 'max-h-96 min-w-[250px] overflow-auto'}`}
        >
          {isSizeFilter ? (
            <SizeFilterGrid
              items={menu.items}
              onOptionSelect={onOptionSelect}
            />
          ) : (
            <MenuOptionsList menu={menu} onOptionSelect={onOptionSelect} />
          )}
        </Popover>
      </FramerMotion>
    </PopoverProvider>
  );
}

function Menu({ menu }: { menu: MenuType }) {
  const activeMenu = menu.items.find((i) => menu.active && i.active);

  return (
    <>
      <Dropdown menu={menu} activeItem={activeMenu} />
      {activeMenu?.sub_menus?.map((subMenu) => (
        <Dropdown
          menu={subMenu}
          activeItem={subMenu.items.find((i) => subMenu.active && i.active)}
          key={subMenu.id}
        />
      ))}
    </>
  );
}

export default function Menus({ menus }: Props) {
  return menus.map((m) => <Menu key={m.id} menu={m} />);
}
